import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormAlert,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { compareMoney, formatPrice } from '../../../utils/money';
import type { AdminWalletDeposit, AdminWalletDepositRefundCancelRequest } from '../../../types/models/Wallet';
import type { ApiResponseAdminWalletDepositRefundCancel } from '../../../types/api/ApiResponseAdminWallet';
import { cancelRefusal, cancellableMaximumHint, normalisePositiveAmount } from './walletUtils';
import type { RefundRefusal } from './walletUtils';

interface WalletCancelRefundModalProps {
  /** The top-up AS THE PAGE DISPLAYED IT when Cancel refund was clicked (its clawback_amount is echoed). */
  deposit: AdminWalletDeposit;
  clientId: number;
  clientName: string;
  onClose: () => void;
  /** Re-read the wallet (top-ups AND ledger): after a cancellation, or when the top-up changed meanwhile. */
  onReload: () => void;
}

interface FieldErrors {
  amount?: string;
  description?: string;
}

/**
 * Cancel a recorded refund — `POST /admin/wallets/{clientId}/deposits/{depositId}/refund/cancel`
 * (docs/api-types/admin-wallets.md « Cancel a recorded refund »): the inverse of Refund, for a refund
 * recorded here whose money cannot go back through Mollie (never made there, recorded by mistake, failed or
 * canceled at Mollie). It credits the wallet and lowers the top-up's clawback in one transaction — the
 * only action that closes the « recorded above Mollie » healthcheck alert.
 *
 * How much can be cancelled depends on what Mollie has refunded or may still refund (the backend's floor),
 * so the amount is not prefilled: the backend answers the maximum as a field error. Same double-submit
 * guard as Refund (`expected_clawback_amount`), and this form never re-posts on its own.
 */
const WalletCancelRefundModal: React.FunctionComponent<WalletCancelRefundModalProps> = ({
  deposit,
  clientId,
  clientName,
  onClose,
  onReload,
}) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [amountInput, setAmountInput] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<{ message: string } & RefundRefusal>();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const amount = normalisePositiveAmount(amountInput);
  // Never more than what was recorded here; the backend's (lower) maximum also accounts for Mollie.
  const overClawback = amount !== null && (compareMoney(amount, deposit.clawback_amount) ?? 1) > 0;
  const trimmedDescription = description.trim();
  const descriptionInvalid = trimmedDescription.length > 255;
  const stale = refusal?.stale === true;
  const canSubmit =
    !saving && !stale && amount !== null && !overClawback && trimmedDescription.length >= 1 && !descriptionInvalid;

  const amountHelper =
    fieldErrors.amount ??
    (amountInput.trim() !== '' && amount === null
      ? 'Enter a positive amount with at most 2 decimals, e.g. 30 or 12.50.'
      : overClawback
        ? `At most ${formatPrice(deposit.clawback_amount)} — what was taken back on this top-up.`
        : null);
  const amountHint = fieldErrors.amount ? cancellableMaximumHint(fieldErrors.amount) : null;
  const descriptionHelper = fieldErrors.description ?? (descriptionInvalid ? 'At most 255 characters.' : null);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const submit = async () => {
    if (!canSubmit || amount === null) {
      return;
    }
    setSaving(true);
    setRefusal(undefined);
    setFieldErrors({});
    const payload: AdminWalletDepositRefundCancelRequest = {
      amount,
      expected_clawback_amount: deposit.clawback_amount,
      description: trimmedDescription,
    };
    try {
      const response = await apiFetch(`/admin/wallets/${clientId}/deposits/${deposit.id}/refund/cancel`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // JSend: branch on body.status, never response.ok — every refusal is an `error` at HTTP 200.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletDepositRefundCancel | null;
      const d = body?.data;
      if (body?.status === 'success' && d?.transaction && d.deposit) {
        addNotification({
          title: 'Recorded refund cancelled',
          srTitle: 'Success alert',
          description: `${formatPrice(amount)} credited back to ${clientName}'s wallet (top-up #${deposit.id}).`,
          variant: 'success',
        });
        setDrawerOpen(true);
        // Right after its commit the backend re-checks Mollie and may claw back a refund it reports: that
        // row is not in the response, so re-read the whole wallet rather than patching the row.
        onReload();
        onClose();
        return;
      }
      const message =
        d?.expected_clawback_amount ??
        d?.body ??
        body?.message ??
        'Could not cancel the recorded refund. Please try again.';
      const outcome = cancelRefusal(message);
      setRefusal({ message, ...outcome });
      setFieldErrors({ amount: d?.amount, description: d?.description });
      if (outcome.stale) {
        onReload();
      }
      addNotification({
        title: 'Recorded refund not cancelled',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setRefusal({
        message,
        hint: `The answer was lost, so the cancellation may or may not have been recorded, and the page has been reloaded. If the top-up now shows ${formatPrice(amount)} less taken back, it was recorded; otherwise reopen this dialog from the reloaded row.`,
        stale: true,
        suggestReceivable: false,
      });
      onReload();
      addNotification({
        title: 'Cancellation status unknown',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      variant="medium"
      isOpen
      onClose={handleClose}
      aria-labelledby="wallet-cancel-refund-title"
      aria-describedby="wallet-cancel-refund-subtitle"
    >
      <ModalHeader
        title="Cancel a recorded refund"
        labelId="wallet-cancel-refund-title"
        description={`Prepaid wallet of ${clientName}`}
        descriptorId="wallet-cancel-refund-subtitle"
      />
      <ModalBody>
        <Form
          id="wallet-cancel-refund-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {refusal && (
            <FormAlert>
              <Alert variant="danger" isInline isLiveRegion title={refusal.message}>
                {refusal.hint}
              </Alert>
            </FormAlert>
          )}
          <Content component="p">
            Use this when a refund was recorded here but its money cannot go back through Mollie: it was never made at
            Mollie or was recorded by mistake, or Mollie&apos;s refund failed or was canceled (e.g. a closed bank
            account). This is also how to close the « recorded above Mollie » healthcheck alert. It credits the wallet
            back and lowers what this top-up shows as taken back.
          </Content>
          <Alert variant="warning" isInline isPlain title="Never use a positive adjustment for this">
            An adjustment credits the wallet but leaves the top-up&apos;s clawback as it is: the alert comes back every
            day, and a Mollie refund made later to silence it would pay the customer twice. If the money must still
            reach the customer, pay it another way (a bank transfer) and record that as a negative adjustment.
          </Alert>
          <DescriptionList isCompact isHorizontal>
            <DescriptionListGroup>
              <DescriptionListTerm>Top-up</DescriptionListTerm>
              <DescriptionListDescription>
                #{deposit.id} · {formatPrice(deposit.amount)} · <code>{deposit.mollie_payment_id}</code>
              </DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Taken back</DescriptionListTerm>
              <DescriptionListDescription>{formatPrice(deposit.clawback_amount)}</DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
          <FormGroup label="Amount to credit back" isRequired fieldId="wallet-cancel-refund-amount">
            <TextInput
              id="wallet-cancel-refund-amount"
              value={amountInput}
              onChange={(_e, v) => setAmountInput(v)}
              validated={amountHelper ? 'error' : 'default'}
              isDisabled={saving || stale}
              aria-describedby="wallet-cancel-refund-amount-helper"
            />
            <FormHelperText>
              <HelperText id="wallet-cancel-refund-amount-helper">
                <HelperTextItem variant={amountHelper ? 'error' : 'default'}>
                  {amountHelper ??
                    'In EUR. The backend checks the payment at Mollie and refuses more than what Mollie has not refunded and will not refund.'}
                </HelperTextItem>
                {amountHint && <HelperTextItem>{amountHint}</HelperTextItem>}
              </HelperText>
            </FormHelperText>
          </FormGroup>
          <FormGroup label="Reason" isRequired fieldId="wallet-cancel-refund-reason">
            <TextInput
              id="wallet-cancel-refund-reason"
              value={description}
              onChange={(_e, v) => setDescription(v)}
              validated={descriptionHelper ? 'error' : 'default'}
              isDisabled={saving || stale}
              aria-describedby="wallet-cancel-refund-reason-helper"
              placeholder="Annulation du remboursement du rechargement (refusé par la banque)"
            />
            <FormHelperText>
              <HelperText id="wallet-cancel-refund-reason-helper">
                <HelperTextItem variant={descriptionHelper ? 'error' : 'default'}>
                  {descriptionHelper ?? 'The statement line the customer sees (1–255 characters).'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="wallet-cancel-refund-form"
          isLoading={saving}
          isDisabled={!canSubmit}
        >
          Cancel the refund
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={saving}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WalletCancelRefundModal;
